import { t } from "@lingui/core/macro";
import { Plural, Trans } from "@lingui/react/macro";
import { Button, Chip } from "@heroui/react";
import type { Time } from "@internationalized/date";
import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useState } from "react";
import { CalendarClock } from "lucide-react";
import { RiDeleteBinLine } from "react-icons/ri";
import { LimeAlert } from "@/Components/NextBase/LimeAlert";
import { LimeCheckbox } from "@/Components/NextBase/LimeCheckbox";
import { LimeInput } from "@/Components/NextBase/LimeInput";
import { LimeInputLabel } from "@/Components/NextBase/LimeInputLabel";
import { LimeLoader } from "@/Components/NextBase/LimeLoader";
import { LimeSelect } from "@/Components/NextBase/LimeSelect";
import { LimeSwitch } from "@/Components/NextBase/LimeSwitch";
import { LimeTextarea } from "@/Components/NextBase/LimeTextarea";
import { LimeTimeInput } from "@/Components/NextBase/LimeTimeInput";
import {
  durationBetween,
  safeParseTime,
} from "@/pages/Dashboard/Calendar/components/hooks/timeOffForm";
import { TimeOffScopeDialog } from "@/pages/Dashboard/Calendar/components/calendar/TimeOffScopeDialog";
import type { TimeOffGranularity } from "@/server-types";
import { cn } from "@/utils";
import { ManageTimeOffTypeModal } from "../../TimeOffTypes/ManageTimeOffTypeModal";
import { resolveTimeOffTypeName } from "../../TimeOffTypes/timeOffTypeDisplayName";
import { TimeOffTypeTiles } from "../../TimeOffTypes/TimeOffTypeTiles";
import { TimeOffOverlapDialog } from "../TimeOffOverlapDialog";
import { Reveal, Section } from "./FormReveal";
import { addDays } from "./timeOffFormValues";
import { formatDuration, formatShortDate } from "./timeOffFormat";
import { TimeOffCompensationSelect } from "./TimeOffCompensationSelect";
import { TimeOffRepeatSection } from "./TimeOffRepeatSection";
import type {
  TimeOffFormState,
  TimeOffResource,
  TimeOffTypeRow,
} from "./useTimeOffForm";

/** The column the note lands in is a VarChar(500). */
const NOTE_MAX_LENGTH = 500;

/** Enough to say which days are missing without listing a whole month. */
const LISTED_DAYS = 7;

function fromTime(value: Time | null): string {
  return value ? value.toString().slice(0, 5) : "";
}

function formatStart(value: string): string {
  return dayjs(value).format("D. M. HH:mm");
}

/** The merged form; the selected type decides the fields, and the caller owns Save. */
export function TimeOffForm({
  state,
  resources,
  locationId,
  canManageTypes,
  onDone,
}: {
  state: TimeOffFormState;
  resources: TimeOffResource[];
  locationId: number | null;
  canManageTypes: boolean;
  /** Called when a dialog the form opened finishes a save or a delete. */
  onDone: () => void;
}) {
  const { form, values, isDay, editing, selectedType } = state;
  const queryClient = useQueryClient();
  const [addingGranularity, setAddingGranularity] =
    useState<TimeOffGranularity | null>(null);

  if (state.isLoadingDetail) return <LimeLoader />;

  const showResources = !isDay && resources.length > 0;
  const hasStaff = values.userIds.length > 0;
  const hasResources = values.resourceIds.length > 0;
  const durationMins = durationBetween(values.startTime, values.endTime);

  const blocksOnlineBooking =
    hasStaff &&
    selectedType != null &&
    (state.isCustomType
      ? !values.allowsOnlineBooking
      : selectedType.blocksOnlineBooking);

  // Read from the refetched list: this render's lists predate the new type.
  async function selectCreatedType(typeId: number) {
    await queryClient.invalidateQueries({ queryKey: ["timeOffTypes"] });
    const created = queryClient
      .getQueriesData<TimeOffTypeRow[]>({ queryKey: ["timeOffTypes"] })
      .flatMap(([, rows]) => rows ?? [])
      .find((type) => type.id === typeId);
    if (created) state.selectType(created);
    else form.setFieldValue("typeId", typeId);
  }

  return (
    <div data-identifier="time-off-form">
      <Section>
        <TimeOffTypeTiles
          timeTypes={state.timeTypes}
          dayTypes={state.dayTypes}
          showTimeGroup={state.allowTimeTypes}
          showDayGroup={state.allowDayTypes}
          value={values.typeId}
          onChange={state.selectType}
          onAddType={
            canManageTypes && editing == null ? setAddingGranularity : undefined
          }
          error={state.errorFor("typeId")}
        />
        {!state.allowTimeTypes && (
          <p className="text-default-400 text-tiny">
            <Trans>Urne odsotnosti dodajte na koledarju.</Trans>
          </p>
        )}
      </Section>

      <Section>
        <div className={cn("grid gap-3", showResources && "sm:grid-cols-2")}>
          <LimeSelect
            label={t`Zaposleni`}
            data-identifier="time-off-staff-picker"
            selectionMode="multiple"
            showSelectAllToggle
            isSearchable
            disallowEmptySelection={false}
            // An edit rewrites the opened person only; the rest of the entry keeps theirs.
            isDisabled={editing != null}
            description={
              editing != null && isDay
                ? t`Urejate odsotnost le tega zaposlenega. Drugi iz istega vnosa ostanejo nespremenjeni.`
                : undefined
            }
            items={state.people.map((person) => ({
              key: String(person.userId),
              label: `${person.name ?? ""} ${person.lastName ?? ""}`.trim(),
            }))}
            value={values.userIds.map(String)}
            onMultipleItemChange={(keys) =>
              form.setFieldValue("userIds", keys.map(Number))
            }
            error={state.errorFor("userIds")}
          />
          {showResources && (
            <LimeSelect
              label={t`Viri`}
              data-identifier="time-off-resource-picker"
              selectionMode="multiple"
              showSelectAllToggle
              isSearchable
              disallowEmptySelection={false}
              isDisabled={editing != null}
              items={resources.map((resource) => ({
                key: String(resource.id),
                label: resource.label ?? "",
              }))}
              value={values.resourceIds.map(String)}
              onMultipleItemChange={(keys) =>
                state.setResourceIds(keys.map(Number))
              }
            />
          )}
        </div>
      </Section>

      <Reveal isOpen={hasResources}>
        <LimeInput
          label={t`Naziv`}
          data-identifier="time-off-title"
          description={
            hasStaff
              ? t`Prevzet iz vrste odsotnosti, lahko ga spremenite.`
              : undefined
          }
          value={values.title}
          onValueChange={(value) => form.setFieldValue("title", value)}
        />
      </Reveal>

      <Reveal isOpen={isDay}>
        <DayFields state={state} />
      </Reveal>

      <Reveal isOpen={!isDay}>
        <Reveal isOpen={values.repeat !== "DATES"}>
          <LimeInput
            type="date"
            label={t`Datum`}
            data-identifier="time-off-date"
            // A new rule starts on the opened occurrence.
            isDisabled={state.ruleChanged}
            value={values.date}
            onValueChange={(value) => form.setFieldValue("date", value)}
            error={state.errorFor("date")}
          />
        </Reveal>
        <Section>
          <div className="grid grid-cols-2 gap-2">
            <LimeTimeInput
              label={t`Začetek`}
              data-identifier="time-off-start-time"
              value={safeParseTime(values.startTime)}
              onChange={(value: Time | null) =>
                form.setFieldValue("startTime", fromTime(value))
              }
              error={state.errorFor("startTime")}
            />
            <LimeTimeInput
              label={t`Konec`}
              data-identifier="time-off-end-time"
              value={safeParseTime(values.endTime)}
              onChange={(value: Time | null) =>
                form.setFieldValue("endTime", fromTime(value))
              }
              error={state.errorFor("endTime")}
            />
          </div>
          <Reveal isOpen={durationMins > 0} spacing="pt-1">
            <p className="text-default-400 text-tiny">
              {selectedType?.defaultDurationMins === durationMins ? (
                <Trans>
                  Trajanje {durationMins} min · privzeto za vrsto{" "}
                  {resolveTimeOffTypeName(selectedType)}
                </Trans>
              ) : (
                <Trans>Trajanje {durationMins} min</Trans>
              )}
            </p>
          </Reveal>
        </Section>
        <TimeOffRepeatSection state={state} locationId={locationId} />
        <Reveal isOpen={state.ruleChanged}>
          <p className="text-default-500 text-tiny">
            {state.batchTargetCount > 1 ? (
              <Trans>
                Novo ponavljanje velja od te ponovitve naprej za vse, za katere
                je bila odsotnost dodana.
              </Trans>
            ) : (
              <Trans>Novo ponavljanje velja od te ponovitve naprej.</Trans>
            )}
          </p>
        </Reveal>
      </Reveal>

      <Reveal isOpen={state.canSeeStaffCost && hasStaff}>
        <TimeOffCompensationSelect state={state} />
      </Reveal>

      <Reveal isOpen={isDay && state.absencePreview != null}>
        <AbsenceSummary state={state} />
      </Reveal>

      <AppointmentWarnings state={state} resources={resources} />

      <Reveal isOpen={state.dailyLimit != null && selectedType != null}>
        {state.dailyLimit != null && selectedType != null && (
          <LimeAlert
            color="danger"
            data-identifier="time-off-daily-limit-alert"
            message={dailyLimitMessage({
              typeName: resolveTimeOffTypeName(selectedType),
              userName: state.nameOf(state.dailyLimit.userId),
              date: formatShortDate(state.dailyLimit.date),
              maxDailyMinutes: state.dailyLimit.maxDailyMinutes,
              totalMinutes: state.dailyLimit.totalMinutes,
            })}
          />
        )}
      </Reveal>

      <Reveal isOpen={state.previewError != null}>
        <LimeAlert
          color="warning"
          data-identifier="time-off-preview-error"
          message={state.previewError}
        />
      </Reveal>

      <Reveal isOpen={state.saveError != null}>
        <LimeAlert
          color="danger"
          data-identifier="time-off-save-error"
          message={state.saveError}
        />
      </Reveal>

      <Section>
        <div className="flex flex-col gap-1">
          <LimeInputLabel
            label={t`Opomba`}
            action={
              <span className="text-default-400 text-tiny">
                {values.note.length}/{NOTE_MAX_LENGTH}
              </span>
            }
          />
          <LimeTextarea
            data-identifier="time-off-note"
            maxLength={NOTE_MAX_LENGTH}
            value={values.note}
            onValueChange={(value) => form.setFieldValue("note", value)}
            maxRows={3}
          />
        </div>
      </Section>

      {/* Only a custom block has no type to inherit this answer from. */}
      <Reveal isOpen={!isDay && hasStaff && state.isCustomType}>
        <LimeCheckbox
          data-identifier="time-off-allows-online-booking"
          isSelected={values.allowsOnlineBooking}
          onValueChange={(value) =>
            form.setFieldValue("allowsOnlineBooking", value)
          }
        >
          <Trans>Spletno naročanje je med to odsotnostjo dovoljeno</Trans>
        </LimeCheckbox>
      </Reveal>

      <Reveal isOpen={blocksOnlineBooking}>
        <p
          className="text-default-400 text-tiny"
          data-identifier="time-off-blocks-online-booking"
        >
          <Trans>Blokira spletno naročanje</Trans>
        </p>
      </Reveal>

      {editing != null && (
        <Section>
          <Button
            variant="light"
            size="sm"
            color="danger"
            type="button"
            data-identifier="time-off-delete"
            startContent={<RiDeleteBinLine />}
            onPress={async () => {
              if (await state.deleteEditing()) onDone();
            }}
          >
            <Trans>Izbriši</Trans>
          </Button>
        </Section>
      )}

      <ManageTimeOffTypeModal
        // Remounted per group, so the form starts on that group's granularity.
        key={addingGranularity ?? "closed"}
        isOpen={addingGranularity != null}
        type={null}
        fixedGranularity={addingGranularity ?? undefined}
        onClose={() => setAddingGranularity(null)}
        onSaved={(created) => {
          if (created != null) void selectCreatedType(created.id);
        }}
      />

      <TimeOffOverlapDialog
        conflict={state.overlapConflict}
        isSaving={state.isSaving}
        onDismiss={state.dismissOverlap}
        onChoose={async (onOverlap) => {
          if (await state.chooseOverlap(onOverlap)) onDone();
        }}
      />

      <TimeOffScopeDialog
        isOpen={state.pendingScope != null}
        isDelete={state.pendingScope?.intent === "delete"}
        onDismiss={state.dismissScope}
        onChoose={async (scope) => {
          if (await state.confirmScope(scope)) onDone();
        }}
      />
    </div>
  );
}

function DayFields({ state }: { state: TimeOffFormState }) {
  const { form, values } = state;
  const isSingleDay = values.date === values.endDate;
  const firstDay = formatShortDate(values.date);
  const lastDay = formatShortDate(values.endDate);

  return (
    <>
      <Section spacing="pb-3">
        <div className="grid grid-cols-2 gap-2">
          <LimeInput
            type="date"
            label={t`Od dne`}
            data-identifier="time-off-starts-on"
            value={values.date}
            onValueChange={(value) => {
              form.setFieldValue("date", value);
              if (values.endDate < value) form.setFieldValue("endDate", value);
            }}
            error={state.errorFor("date")}
          />
          <LimeInput
            type="date"
            label={t`Do dne`}
            data-identifier="time-off-ends-on"
            value={values.endDate}
            onValueChange={(value) => form.setFieldValue("endDate", value)}
            error={state.errorFor("endDate")}
          />
        </div>
      </Section>

      <Section spacing="pb-3">
        <LimeSwitch
          isCompact
          title={t`Ves dan`}
          data-identifier="time-off-all-day"
          isSelected={values.allDay}
          onValueChange={(value: boolean) => {
            form.setFieldValue("allDay", value);
            if (!value && values.dayStartTime === "") {
              form.setFieldValue("dayStartTime", "12:00");
            }
          }}
        />
      </Section>

      <Reveal isOpen={!values.allDay} spacing="pb-3">
        <div className="grid grid-cols-2 gap-2">
          <LimeTimeInput
            label={isSingleDay ? t`Od ure` : t`Od ure (prvi dan, ${firstDay})`}
            data-identifier="time-off-day-start-time"
            description={t`Prazno: od začetka izmene`}
            value={safeParseTime(values.dayStartTime)}
            onChange={(value: Time | null) =>
              form.setFieldValue("dayStartTime", fromTime(value))
            }
            error={state.errorFor("dayStartTime")}
          />
          <LimeTimeInput
            label={isSingleDay ? t`Do ure` : t`Do ure (zadnji dan, ${lastDay})`}
            data-identifier="time-off-day-end-time"
            description={t`Prazno: do konca izmene`}
            value={safeParseTime(values.dayEndTime)}
            onChange={(value: Time | null) =>
              form.setFieldValue("dayEndTime", fromTime(value))
            }
            error={state.errorFor("dayEndTime")}
          />
        </div>
      </Reveal>
    </>
  );
}

/** Per person, from the dry run: shifts and hours taken, part days, and days without a shift. */
function AbsenceSummary({ state }: { state: TimeOffFormState }) {
  const { values, absencePreview } = state;
  if (absencePreview == null) return null;

  // A re-save keeps the days already under way, so they are neither listed nor judged.
  const editableFrom = state.firstEditableDate;
  const rangeDates: string[] = [];
  for (
    let date = editableFrom;
    date <= values.endDate && rangeDates.length <= 366;
    date = addDays(date, 1)
  ) {
    // Today's row may have started and been kept, which the dry run cannot tell from no shift.
    if (date !== state.todayAtLocation) rangeDates.push(date);
  }

  const people = absencePreview.people.filter(
    (person) => person.rows.length > 0,
  );
  const withoutShifts = absencePreview.userIdsWithoutShifts;
  const alreadyAbsent = absencePreview.userIdsAlreadyAbsent;

  return (
    <div
      className={cn(
        "flex flex-col gap-2 rounded-lg bg-default-50 p-3 transition-opacity",
        state.isPreviewing && "opacity-60",
      )}
      data-identifier="time-off-absence-summary"
    >
      {people.map((person) => {
        const rowDates = new Set(person.rows.map((row) => row.date));
        const missing = rangeDates.filter((date) => !rowDates.has(date));
        const first = person.rows[0];
        const last = person.rows[person.rows.length - 1];
        const partial = [
          !values.allDay &&
          values.dayStartTime !== "" &&
          values.date >= editableFrom &&
          first?.date === values.date
            ? t`${formatShortDate(first.date)} od ${first.from.slice(11, 16)}`
            : null,
          !values.allDay &&
          values.dayEndTime !== "" &&
          last?.date === values.endDate &&
          last !== first
            ? t`${formatShortDate(last.date)} do ${last.to.slice(11, 16)}`
            : null,
        ].filter((line): line is string => line != null);
        const hours = formatDuration(person.totalMinutes);
        const missingList = missing
          .slice(0, LISTED_DAYS)
          .map(formatShortDate)
          .join(", ");

        return (
          <div key={person.userId} className="flex flex-col text-sm">
            <span>
              <span className="font-semibold">
                {state.nameOf(person.userId)}
              </span>
              {": "}
              <Plural
                value={person.rows.length}
                one="# izmena"
                two="# izmeni"
                few="# izmene"
                other="# izmen"
              />
              {` · ${hours}`}
            </span>
            {partial.length > 0 && (
              <span className="text-default-500 text-tiny">
                <Trans>Delni dan: {partial.join(", ")}</Trans>
              </span>
            )}
            {missing.length > 0 && (
              <span className="text-default-500 text-tiny">
                <Trans>Brez izmene: {missingList}</Trans>
                {missing.length > LISTED_DAYS ? " …" : ""}
              </span>
            )}
          </div>
        );
      })}

      {withoutShifts.length > 0 && (
        <p className="text-tiny text-warning-600">
          <Trans>
            Brez izmene v tem obdobju, zato jim ne bo dodano:{" "}
            {withoutShifts.map(state.nameOf).join(", ")}
          </Trans>
        </p>
      )}
      {alreadyAbsent.length > 0 && (
        <p className="text-tiny text-warning-600">
          <Trans>
            Že odsotni ob vseh izmenah:{" "}
            {alreadyAbsent.map(state.nameOf).join(", ")}
          </Trans>
        </p>
      )}
    </div>
  );
}

/** Appointments inside the time off, per person or resource; they stay on the calendar. */
function AppointmentWarnings({
  state,
  resources,
}: {
  state: TimeOffFormState;
  resources: TimeOffResource[];
}) {
  const [shown, setShown] = useState<string | null>(null);

  const entries = (() => {
    if (state.absencePreview) {
      return state.absencePreview.people.map((person) => ({
        key: `u${person.userId}`,
        name: state.nameOf(person.userId),
        count: person.appointmentCount,
        starts: person.appointmentStarts,
      }));
    }
    if (state.timePreview && state.editing == null) {
      return state.timePreview.targets.map((target) => ({
        key:
          target.kind === "staff"
            ? `u${target.userId}`
            : `r${target.resourceId}`,
        name:
          target.kind === "staff"
            ? state.nameOf(target.userId)
            : (resources.find((resource) => resource.id === target.resourceId)
                ?.label ?? ""),
        count: target.appointmentCount,
        starts: target.appointmentStarts,
      }));
    }
    return [];
  })().filter((entry) => entry.count > 0);

  return (
    <Reveal isOpen={entries.length > 0}>
      <div
        className={cn(
          "flex flex-col gap-2 rounded-lg border border-warning-200 bg-warning-50 p-3 text-sm transition-opacity",
          state.isPreviewing && "opacity-60",
        )}
        data-identifier="time-off-appointment-warning"
      >
        <p className="flex items-center gap-2 font-medium text-warning-700">
          <CalendarClock size={16} className="shrink-0" />
          <Trans>Termini v tem obdobju</Trans>
        </p>

        <ul className="flex flex-col divide-y divide-warning-100">
          {entries.map((entry) => (
            <li key={entry.key} className="flex flex-col py-1.5">
              <div className="flex items-center justify-between gap-2">
                <span className="min-w-0 truncate font-medium">
                  {entry.name}
                </span>
                <span className="flex shrink-0 items-center gap-2">
                  <Chip size="sm" variant="flat" color="warning">
                    <Plural
                      value={entry.count}
                      one="# termin"
                      two="# termina"
                      few="# termine"
                      other="# terminov"
                    />
                  </Chip>
                  <Button
                    size="sm"
                    variant="light"
                    color="primary"
                    className="h-auto min-w-0 px-1 py-0.5"
                    data-identifier="time-off-appointments-show"
                    onPress={() =>
                      setShown((current) =>
                        current === entry.key ? null : entry.key,
                      )
                    }
                  >
                    {shown === entry.key ? (
                      <Trans>Skrij</Trans>
                    ) : (
                      <Trans>Prikaži</Trans>
                    )}
                  </Button>
                </span>
              </div>
              <Reveal isOpen={shown === entry.key} spacing="pt-1.5">
                <span className="flex flex-wrap gap-1">
                  {entry.starts.map((start) => (
                    <Chip key={start} size="sm" variant="bordered">
                      {formatStart(start)}
                    </Chip>
                  ))}
                  {entry.count > entry.starts.length && (
                    <Chip size="sm" variant="light">
                      <Trans>in še {entry.count - entry.starts.length}</Trans>
                    </Chip>
                  )}
                </span>
              </Reveal>
            </li>
          ))}
        </ul>

        <p className="text-default-500 text-tiny">
          <Trans>
            Termini ostanejo na koledarju — odsotnost jih ne prestavi in ne
            odpove.
          </Trans>
        </p>
      </div>
    </Reveal>
  );
}

function dailyLimitMessage({
  typeName,
  userName,
  date,
  maxDailyMinutes,
  totalMinutes,
}: {
  typeName: string;
  userName: string;
  date: string;
  maxDailyMinutes: number;
  totalMinutes: number;
}) {
  return t`Dnevna omejitev za ${typeName} je ${maxDailyMinutes} min na zaposlenega. Skupaj za ${userName} dne ${date}: ${totalMinutes} min.`;
}
